// C.1 — общие хелперы для DSL-эффектов из v6.
//
// Каждая функция принимает (state, sourceCard, target, value, opts).
// Это «строительные блоки» для способностей. Карты подключают нужные
// хелперы и комбинируют — но КАЖДАЯ карта остаётся отдельным JS-файлом
// (правило CLAUDE.md «способности — кодом»).
//
// Часто используется в комбинации с composite.js (парсер) и
// conditions.js (проверка condition) и targets.js (резолвер target'а).

import { applyDamage } from '../combat.js';
import { healUnit } from '../abilities/livlife/heal.js';
import { applyPermanentBuff, applyTemporaryBuff } from '../buffs.js';
import { applySilence } from '../keywords/silence.js';
import { addPermanentKeyword } from '../keywords/permanent.js';
import { addInfectedStack } from '../keywords/infected.js';
import { applyImmunity, applyImmuneFirstHit } from '../keywords/immune.js';
import { summonUnit } from '../abilities/global/summon.js';

// === Парсинг N/N/N стат-баффа ===
// "1/0/2" → { atk: 1, armor: 0, hp: 2 }
// "-0/1/0" → { atk: 0, armor: -1, hp: 0 }   (только armor)
export function parseStatDelta(value) {
  if (typeof value !== 'string') return { atk: 0, armor: 0, hp: 0 };
  const parts = value.split('/').map(s => Number(s.trim()));
  return {
    atk:   Number.isFinite(parts[0]) ? parts[0] : 0,
    armor: Number.isFinite(parts[1]) ? parts[1] : 0,
    hp:    Number.isFinite(parts[2]) ? parts[2] : 0
  };
}

// === Парсинг числа ===
export function parseNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// === damage ===
// value = число (по обычной формуле) или "current_influence" / "hero_atk" / ...
//   target_atk / target_max_hp — внешние карты сами вычисляют и передают opts.amount.
export function applyDamageEffect(state, source, target, value, opts = {}) {
  if (!target) return null;
  let dmg;
  if (typeof value === 'number') dmg = value;
  else if (opts?.amountOverride != null) dmg = opts.amountOverride;
  else if (typeof value === 'string') {
    if (value === 'hero_atk') dmg = state?.players?.[source?.ownerId]?.hero?.atk ?? 0;
    else if (value === 'target_atk') dmg = target?.current?.atk ?? 0;
    else if (value === 'target_max_hp') dmg = target?.base?.hp ?? 0;
    else dmg = parseNum(value);
  } else dmg = 0;
  if (dmg <= 0) return null;
  return applyDamage(target, dmg, {
    state,
    source,
    pierce: !!opts?.pierce
  });
}

// === heal ===
export function applyHealEffect(state, source, target, value, opts = {}) {
  if (!target) return null;
  const amount = typeof value === 'number' ? value : parseNum(value);
  if (amount <= 0) return null;
  return healUnit(state, target, amount, source, opts);
}

// === heal_to_full ===
export function applyHealToFullEffect(state, source, target) {
  if (!target) return null;
  const cur = target.current?.hp ?? target.hp ?? 0;
  const max = target.base?.hp ?? target.maxHp ?? cur;
  const amount = Math.max(0, max - cur);
  if (amount === 0) return null;
  return healUnit(state, target, amount, source);
}

// === buff_perm / buff_self_perm ===
// value формата "atk/armor/hp"
export function applyBuffPermEffect(state, source, target, value, opts = {}) {
  if (!target) return false;
  const delta = parseStatDelta(value);
  return applyPermanentBuff(
    state, target, delta,
    opts?.sourceId || source?.id || 'buff',
    opts?.sourceLabel || source?.name_ru || 'бафф'
  );
}

// === buff_temp ===
// value формата "atk/armor/hp", opts.duration в ходах (n_turns)
export function applyBuffTempEffect(state, source, target, value, opts = {}) {
  if (!target) return false;
  const delta = parseStatDelta(value);
  const turns = opts?.turns ?? 1;
  const expiresAtTurn = (state?.currentTurn ?? 1) + turns;
  return applyTemporaryBuff(
    state, target, delta, expiresAtTurn,
    opts?.sourceId || source?.id || 'buff_temp',
    opts?.sourceLabel || source?.name_ru || 'врем. бафф'
  );
}

// === debuff_stats ===
// value формата "atk/armor/hp" с отрицательными значениями (или используется как есть).
export function applyDebuffStatsEffect(state, source, target, value) {
  if (!target) return false;
  const delta = parseStatDelta(value);
  // Применяем как permanent buff с отрицательными значениями.
  return applyPermanentBuff(
    state, target,
    { atk: delta.atk, armor: delta.armor, hp: delta.hp },
    source?.id || 'debuff',
    source?.name_ru || 'дебафф'
  );
}

// === silence ===
export function applySilenceEffect(state, source, target) {
  if (!target) return false;
  return applySilence(state, target, source?.id || 'silence_source').ok;
}

// === give_keyword ===
export function applyGiveKeywordEffect(state, source, target, keyword, opts = {}) {
  if (!target || !keyword) return false;
  return addPermanentKeyword(
    state, target, keyword,
    opts?.sourceId || source?.id || 'kw_grant',
    opts?.sourceLabel || source?.name_ru || 'выдача keyword'
  );
}

// === infect ===
export function applyInfectEffect(state, source, target, value) {
  if (!target) return 0;
  const count = typeof value === 'number' ? value : parseNum(value, 1);
  return addInfectedStack(state, target, source?.id || 'infect', count);
}

// === immune (длительный / first-hit) ===
export function applyImmuneEffect(state, source, target, value, opts = {}) {
  if (!target) return false;
  if (opts?.firstHit) return applyImmuneFirstHit(state, target, source?.id || 'immune');
  const turns = parseNum(value, 1);
  const expiresAtTurn = (state?.currentTurn ?? 1) + turns;
  return applyImmunity(state, target, source?.id || 'immune', expiresAtTurn);
}

// === summon ===
// value = cardId. opts.line / opts.slot — желаемая позиция.
export function applySummonEffect(state, source, value, opts = {}) {
  if (!value) return null;
  const ownerIdx = source?.ownerId;
  if (ownerIdx == null) return null;
  const owner = state.players?.[ownerIdx];
  if (!owner) return null;

  // Если задана конкретная линия — пробуем там.
  if (opts?.line != null && opts?.slot != null) {
    return summonUnit(state, ownerIdx, value, opts.line, opts.slot);
  }
  if (opts?.line != null) {
    const row = owner.field[opts.line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) return summonUnit(state, ownerIdx, value, opts.line, s);
    }
    // Нет места на желаемой линии — пробуем другую (опционально).
    if (opts?.fallbackOtherLine) {
      const other = 1 - opts.line;
      const row2 = owner.field[other] || [];
      for (let s = 0; s < row2.length; s++) {
        if (row2[s] === null) return summonUnit(state, ownerIdx, value, other, s);
      }
    }
    return null;
  }
  // Произвольное свободное место.
  for (let line = 0; line < 2; line++) {
    const row = owner.field[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) return summonUnit(state, ownerIdx, value, line, s);
    }
  }
  return null;
}

// === draw ===
export function applyDrawEffect(state, source, count = 1) {
  const ownerIdx = source?.ownerId;
  if (ownerIdx == null) return 0;
  const p = state.players?.[ownerIdx];
  if (!p) return 0;
  let drawn = 0;
  const maxH = p.maxHand ?? 10;
  for (let i = 0; i < count; i++) {
    if (typeof p.drawOne === 'function') {
      // БАГ-фикс (Этап 56.56): p.drawOne() ВОЗВРАЩАЕТ карту, но НЕ кладёт её в
      // руку (это делает вызывающий — см. phaseDraw). Раньше результат
      // игнорировался → карта уходила из колоды мимо руки («Прайс-лист»
      // «добери 2» не работал: колода -2, рука +0).
      const c = p.drawOne();
      if (c) {
        if (p.hand && p.hand.length < maxH) p.hand.push(c);
        else p.discard?.push(c); // рука полна — сгорает в сброс
        drawn++;
      }
    } else if (p.deck && p.deck.length > 0 && p.hand && p.hand.length < maxH) {
      const card = p.deck.shift();
      p.hand.push(card);
      drawn++;
    }
  }
  return drawn;
}

// === discard ===
export function applyDiscardEffect(state, source, count = 1) {
  const ownerIdx = source?.ownerId;
  if (ownerIdx == null) return 0;
  const p = state.players?.[ownerIdx];
  if (!p?.hand || p.hand.length === 0) return 0;
  let discarded = 0;
  for (let i = 0; i < count && p.hand.length > 0; i++) {
    const idx = state?.rng?.int ? state.rng.int(p.hand.length) : 0;
    const [card] = p.hand.splice(idx, 1);
    if (p.discard) p.discard.push(card);
    discarded++;
  }
  return discarded;
}

// === freeze ===
// value = число ходов
export function applyFreezeEffect(state, source, target, value) {
  if (!target) return false;
  const turns = parseNum(value, 1);
  if (turns <= 0) return false;
  target.frozenUntilTurn = Math.max(target.frozenUntilTurn || 0, (state?.currentTurn ?? 0) + turns);
  if (state?.log) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    state.log.add(`❄ ${target.name_ru || target.id} [${ownerName}]: заморозка на ${turns} ход(ов)`);
  }
  return true;
}

// === armor_break ===
export function applyArmorBreakEffect(state, source, target) {
  if (!target) return false;
  if (target.current) target.current.armor = 0;
  if (typeof target.persistentMaxArmor === 'number') target.persistentMaxArmor = 0;
  if (typeof target.persistentArmor === 'number') target.persistentArmor = 0;
  target.armorBroken = true;
  if (state?.log) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    state.log.add(`💥 ${target.name_ru || target.id} [${ownerName}]: вся броня снята`);
  }
  return true;
}

// === overshield (временная +N к current.armor сверх max) ===
export function applyOvershieldEffect(state, source, target, value) {
  if (!target?.current) return false;
  const amount = parseNum(value, 0);
  if (amount <= 0) return false;
  target.current.armor = (target.current.armor || 0) + amount;
  if (state?.log) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    state.log.add(`🛡 ${target.name_ru || target.id} [${ownerName}]: +${amount} брони (overshield)`);
  }
  return true;
}
